import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'

import type {
  EventDetailAttendee,
  EventDetailExhibitor,
  EventDetailExhibitorMember,
  EventExhibitorMemberRole,
  EventExhibitorMembership,
  EventDetailPerson,
  EventDetailPersonLink,
  EventDetailUserProfile,
  EventDetailProfileBooth,
} from '../types/event-detail'
import type {
  EventsManagementAttendee,
  EventsManagementExhibitor,
  EventsManagementExhibitorMember,
  EventsManagementExhibitorMembership,
  EventsManagementExhibitorTier,
  EventsManagementLink,
  EventsManagementUser,
  EventsManagementProfileBooth,
  EventsManagementSpeaker,
  EventParticipantReference,
  EventParticipantRole,
  EventUserProfileTarget,
} from '../types/events-management'

const EVENT_PARTICIPANT_ROLES: readonly EventParticipantRole[] = ['attendee', 'speaker', 'exhibitor_member']

export const isEventParticipantRole = (value: unknown): value is EventParticipantRole =>
  typeof value === 'string' && (EVENT_PARTICIPANT_ROLES as readonly string[]).includes(value)

/** Opens the profile by portal account when there is one, otherwise by participant id and role. */
export const toUserProfileTarget = (
  clientPortalUserId: string | undefined,
  participant: EventParticipantReference
): EventUserProfileTarget => {
  const userId = clientPortalUserId?.trim()
  return userId ? { id: userId } : { id: participant.participantId, role: participant.role }
}

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value)
    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

const bySortOrder = <T extends { readonly sortOrder?: number }>(rows: readonly T[]): T[] =>
  rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => (left.row.sortOrder ?? 0) - (right.row.sortOrder ?? 0) || left.index - right.index)
    .map(({ row }) => row)

const firstOf = (...values: readonly (string | undefined)[]): string => values.find(Boolean) ?? ''

const firstTag = (value: string | readonly string[] | undefined): string =>
  typeof value === 'string' ? value : (value?.find(Boolean) ?? '')

/** Only https links with a title are shown; they open in a new tab. */
export const toPersonLinks = (links: readonly EventsManagementLink[] | undefined): EventDetailPersonLink[] =>
  (links ?? []).flatMap(link => (link.title && link.url && isExternalHttpsRedirect(link.url) ? [{ title: link.title, url: link.url }] : []))

const toSpeakerLocation = (speaker: EventsManagementSpeaker): string => {
  if (typeof speaker.location === 'string') return speaker.location
  const city = firstOf(speaker.city, speaker.location?.city)
  const region = firstOf(speaker.region, speaker.state, speaker.location?.region, speaker.location?.state)
  return [city, region].filter(Boolean).join(', ')
}

const toSpeaker = (speaker: EventsManagementSpeaker): EventDetailPerson => {
  const jobLine = [
    firstOf(speaker.title, speaker.jobTitle, speaker.designation),
    firstOf(speaker.company, speaker.organization, speaker.companyName),
  ]
    .filter(Boolean)
    .join(', ')
  const tag = firstTag(speaker.label) || firstTag(speaker.tag) || firstTag(speaker.tags)
  const tags = [...new Set((speaker.tags ?? []).filter(Boolean))]
  const location = toSpeakerLocation(speaker)
  const bio = firstOf(speaker.description, speaker.bio)
  const links = toPersonLinks(speaker.socialLinks?.customLinks)
  const isRootRelative = (url: string) => url.startsWith('/') && !url.startsWith('//')
  const imageUrl = speaker.imageUrl && (isHttpUrl(speaker.imageUrl) || isRootRelative(speaker.imageUrl)) ? speaker.imageUrl : undefined

  return {
    id: speaker.id,
    name: speaker.name,
    role: jobLine || speaker.tagline || '',
    ...(imageUrl ? { imageUrl } : {}),
    ...(tag ? { tag } : {}),
    ...(tags.length > 0 ? { tags } : {}),
    ...(location ? { location } : {}),
    ...(bio ? { bio } : {}),
    ...(links.length > 0 ? { links } : {}),
    ...(speaker.clientPortalUserId ? { clientPortalUserId: speaker.clientPortalUserId } : {}),
  }
}

export const toEventSpeakers = (speakers: readonly EventsManagementSpeaker[]): EventDetailPerson[] => bySortOrder(speakers).map(toSpeaker)

/** "Title, Company" when either is set; otherwise the role, except the generic "attendee" label. */
const toAttendeeRole = (attendee: EventsManagementAttendee): string => {
  const jobLine = [attendee.title, attendee.company].filter(Boolean).join(', ')
  if (jobLine) return jobLine
  return attendee.role && attendee.role.toLowerCase() !== 'attendee' ? attendee.role : ''
}

const toAttendee = (attendee: EventsManagementAttendee): EventDetailAttendee => {
  const role = toAttendeeRole(attendee)
  const avatar = attendee.imageUrl || attendee.avatar
  return {
    id: attendee.participantId,
    name: attendee.displayName,
    ...(attendee.isSelf ? { isSelf: true } : {}),
    ...(role ? { role } : {}),
    ...(avatar && isHttpUrl(avatar) ? { imageUrl: avatar } : {}),
    ...(attendee.slug ? { slug: attendee.slug } : {}),
    ...(attendee.clientPortalUserId ? { clientPortalUserId: attendee.clientPortalUserId } : {}),
  }
}

export const toEventAttendees = (attendees: readonly EventsManagementAttendee[]): EventDetailAttendee[] => attendees.map(toAttendee)

const titleCase = (value: string): string =>
  value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')

/** Tier colors reach an inline `style`, so anything but a literal hex is discarded. */
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const toTier = (tierId: string | undefined, tiers: readonly EventsManagementExhibitorTier[]) => {
  if (!tierId) return { name: '' }
  const tier = tiers.find(entry => entry.id === tierId)
  return {
    name: tier?.name || titleCase(tierId),
    ...(tier?.color && HEX_COLOR_PATTERN.test(tier.color) ? { color: tier.color } : {}),
    ...(tier?.order !== undefined ? { order: tier.order } : {}),
  }
}

const toExhibitor = (exhibitor: EventsManagementExhibitor, tiers: readonly EventsManagementExhibitorTier[]): EventDetailExhibitor => {
  const tier = toTier(exhibitor.tierId, tiers)
  const tierRank = exhibitor.tierRank ?? tier.order
  const httpsOnly = (url: string | undefined) => (url && isExternalHttpsRedirect(url) ? url : undefined)
  const websiteUrl = httpsOnly(exhibitor.websiteUrl)
  const logoUrl = httpsOnly(exhibitor.logoUrl)
  // An untitled social link is labelled by its url.
  const links = toPersonLinks(exhibitor.socialLinks?.customLinks?.map(link => ({ ...link, title: link.title || link.url })))

  return {
    id: exhibitor.id,
    name: exhibitor.name,
    booth: exhibitor.booth?.number ?? '',
    initial: exhibitor.name.charAt(0).toUpperCase(),
    ...(tier.name ? { tag: tier.name } : {}),
    ...(tier.color ? { tagColor: tier.color } : {}),
    ...(tierRank !== undefined ? { tierRank } : {}),
    ...(exhibitor.description ? { description: exhibitor.description } : {}),
    ...(websiteUrl ? { websiteUrl } : {}),
    ...(logoUrl ? { logoUrl } : {}),
    ...(exhibitor.contactEmail?.includes('@') ? { contactEmail: exhibitor.contactEmail } : {}),
    ...(exhibitor.booth?.note ? { boothNote: exhibitor.booth.note } : {}),
    ...(links.length > 0 ? { links } : {}),
    ...(exhibitor.bookmarked === true ? { bookmarked: true } : {}),
    showAboutAndLinks: exhibitor.showAboutAndLinks !== false,
  }
}

/** Ranked by tier first, then by the authored sort order within a tier. */
export const toEventExhibitors = (
  exhibitors: readonly EventsManagementExhibitor[],
  tiers: readonly EventsManagementExhibitorTier[]
): EventDetailExhibitor[] =>
  bySortOrder(exhibitors)
    .map((exhibitor, index) => ({ exhibitor: toExhibitor(exhibitor, tiers), index }))
    .sort(
      (left, right) =>
        (left.exhibitor.tierRank ?? Number.MAX_SAFE_INTEGER) - (right.exhibitor.tierRank ?? Number.MAX_SAFE_INTEGER) ||
        left.index - right.index
    )
    .map(({ exhibitor }) => exhibitor)

export const toEventExhibitor = (
  exhibitor: EventsManagementExhibitor | undefined,
  tiers: readonly EventsManagementExhibitorTier[]
): EventDetailExhibitor | undefined => (exhibitor ? toExhibitor(exhibitor, tiers) : undefined)

/** Unknown or missing roles fail closed to `member` — admin is never granted from an unrecognised value. */
export const toExhibitorMemberRole = (value: string | undefined): EventExhibitorMemberRole =>
  value?.trim().toLowerCase() === 'admin' ? 'admin' : 'member'

/** One team shared by the public profile and the exhibitor center's team table. */
export const toExhibitorMembers = (members: readonly EventsManagementExhibitorMember[]): EventDetailExhibitorMember[] =>
  members.map(member => ({
    id: member.id,
    name: member.name,
    jobTitle: member.jobTitle ?? '',
    email: member.email ?? '',
    memberRole: toExhibitorMemberRole(member.boothRole),
    ...(member.imageUrl && isExternalHttpsRedirect(member.imageUrl) ? { imageUrl: member.imageUrl } : {}),
    ...(member.clientPortalUserId ? { clientPortalUserId: member.clientPortalUserId } : {}),
  }))

/** A user's profile page; only an https avatar and https links are kept. */
export const toEventUserProfile = (person: EventsManagementUser | undefined): EventDetailUserProfile | undefined => {
  if (!person) return undefined
  const title = person.title?.trim()
  const company = person.company?.trim()
  const label = person.label?.trim()
  const bio = person.bio?.trim()
  const avatar = person.avatarUrl && isExternalHttpsRedirect(person.avatarUrl) ? person.avatarUrl : undefined
  return {
    clientPortalUserId: person.clientPortalUserId ?? '',
    fullName: person.name?.trim() ?? '',
    ...(title ? { title } : {}),
    ...(company ? { company } : {}),
    ...(avatar ? { avatar } : {}),
    ...(label ? { label } : {}),
    ...(bio ? { bio } : {}),
    links: toPersonLinks(person.socials?.customLinks ?? undefined),
    isSelf: person.isSelf === true,
  }
}

/** The booths a person staffs, flattened the way the exhibitor directory flattens a booth. */
export const toProfileBooths = (booths: readonly EventsManagementProfileBooth[]): EventDetailProfileBooth[] =>
  booths.map(booth => ({
    id: booth.id,
    name: booth.name,
    booth: booth.booth?.number ?? '',
    ...(booth.logoUrl && isExternalHttpsRedirect(booth.logoUrl) ? { logoUrl: booth.logoUrl } : {}),
    ...(booth.booth?.note ? { boothNote: booth.booth.note } : {}),
    ...(booth.bookmarked === true ? { bookmarked: true } : {}),
  }))

/** The exhibitors the viewer belongs to, with the permission each membership grants. */
export const toExhibitorMemberships = (memberships: readonly EventsManagementExhibitorMembership[]): EventExhibitorMembership[] =>
  memberships.map(membership => ({
    exhibitorId: membership.exhibitorId,
    exhibitorName: membership.exhibitorName ?? '',
    role: toExhibitorMemberRole(membership.memberRole),
    ...(membership.logoUrl && isExternalHttpsRedirect(membership.logoUrl) ? { logoUrl: membership.logoUrl } : {}),
  }))

export const exhibitorWebsiteLabel = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Two-letter logo placeholder: initials of the first two words, or the first two letters. */
export const exhibitorMark = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase()
  return name.trim().slice(0, 2).toUpperCase()
}
